import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { ClassificationDtoIn } from '@/models/in/ClassificationDtoIn';
import FileUpload from './components/FileUpload';
import addIcon from '@/assets/icons/add.svg';
import type { SupportedFormData } from './schemas/supportedFormSchema';

const DocumentsSection = () => {
  const { t } = useTranslation();
  const { setValue, watch } = useFormContext<SupportedFormData>();
  const { data: classifications } = useGetAuthApi<ClassificationDtoIn[]>('classifications');
  
  const contactClassifications = watch('contactClassifications') || [];

  const getClassificationById = (classificationId: number): ClassificationDtoIn | undefined => {
    return classifications?.find(c => c.id === classificationId);
  };

  const addAdditionalDocument = (contactClassificationIndex: number) => {
    const currentDocs = contactClassifications[contactClassificationIndex].additionalDocuments || [];
    const updatedDocuments = [
      ...currentDocs,
      { file: null, customDescription: '' }
    ];
    setValue(`contactClassifications.${contactClassificationIndex}.additionalDocuments`, updatedDocuments);
  };

  const removeAdditionalDocument = (contactClassificationIndex: number, docIndex: number) => {
    const currentDocs = contactClassifications[contactClassificationIndex].additionalDocuments || [];
    const updatedDocuments = currentDocs.filter((_, i) => i !== docIndex);
    setValue(`contactClassifications.${contactClassificationIndex}.additionalDocuments`, updatedDocuments);
  };

  const handleAdditionalDocumentChange = (
    contactClassificationIndex: number,
    docIndex: number,
    value: { file: File | null; customDescription: string }
  ) => {
    const currentDocs = contactClassifications[contactClassificationIndex].additionalDocuments || [];
    const updatedDocuments = [...currentDocs];
    updatedDocuments[docIndex] = value;
    setValue(`contactClassifications.${contactClassificationIndex}.additionalDocuments`, updatedDocuments);
  };

  const handleRequiredDocumentChange = (
    contactClassificationIndex: number,
    requiredDocName: string,
    file: File | null
  ) => {
    const currentDocs = contactClassifications[contactClassificationIndex].requiredDocuments || [];
    const filteredDocs = currentDocs.filter(doc => doc.requiredDocName !== requiredDocName);
    
    const updatedDocuments = file
      ? [...filteredDocs, { file, requiredDocName }]
      : filteredDocs;
    
    setValue(`contactClassifications.${contactClassificationIndex}.requiredDocuments`, updatedDocuments);
  };

  return (
    <div className="flex flex-col gap-6 w-[700px] max-md:w-full">
      <div className="flex flex-col gap-2">
        <h4 className="body-lr-semibold text-(--font-sub-title-2)">העלאת מסמכים ואישורים רלוונטים</h4>
      </div>

      {contactClassifications.length === 0 ? (
        <p className="body-md-regular text-(--font-body-3)">{t('noClassificationsSelected')}</p>
      ) : (
        contactClassifications.map((contactClassification, contactClassificationIndex) => {
          const classification = getClassificationById(contactClassification.classificationId);
          
          // Skip Basic classifications - they don't require documents
          if (!classification || classification.name === 'Basic') {
            return null;
          }

          const hasRequiredDocuments = classification.requiredDocuments && classification.requiredDocuments.length > 0;
          const requiredDocs = contactClassification.requiredDocuments || [];
          const additionalDocs = contactClassification.additionalDocuments || [];

          return (
            <div key={`classification-${contactClassification.classificationId}`} className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <h5 className="body-md-semibold text-(--font-body-1)">{t(classification.name)}</h5>
                {hasRequiredDocuments && (
                  <p className="body-sm-regular text-(--font-heading)">יש להעלות לפחות אחד מהמסמכים הבאים:</p>
                )}
              </div>
              
              <div className="flex flex-col gap-4">
                {/* Required documents */}
                {hasRequiredDocuments && classification.requiredDocuments.map((requiredDoc, docIndex) => {
                  const fileForThisRequiredDoc = requiredDocs
                    .find(doc => doc.requiredDocName === requiredDoc)?.file || null;

                  return (
                    <FileUpload
                      key={`required-${requiredDoc}-${docIndex}`}
                      placeholder={t(`ClassificationsRequiredDocuments.${requiredDoc}`)}
                      value={fileForThisRequiredDoc}
                      onChange={(file) => handleRequiredDocumentChange(contactClassificationIndex, requiredDoc, file)}
                    />
                  );
                })}

                {/* Additional documents */}
                {additionalDocs.map((doc, docIndex) => {
                  return (
                    <FileUpload
                      key={`additional-${contactClassificationIndex}-${docIndex}`}
                      placeholder={t('documentName')}
                      value={doc.file}
                      onChange={(file) => 
                        handleAdditionalDocumentChange(contactClassificationIndex, docIndex, {
                          file,
                          customDescription: doc.customDescription || '',
                        })
                      }
                      customDescription={{
                        description: doc.customDescription || '',
                        onDescriptionChange: (description) => 
                          handleAdditionalDocumentChange(contactClassificationIndex, docIndex, {
                            file: doc.file,
                            customDescription: description,
                          })
                        ,
                        onRemove: () => removeAdditionalDocument(contactClassificationIndex, docIndex),
                      }}
                    />
                  );
                })}

                {/* Add additional document button */}
                <button
                  type="button"
                  onClick={() => addAdditionalDocument(contactClassificationIndex)}
                  className="flex items-center justify-center gap-2 h-11 px-4 py-2 rounded-md border border-dashed border-(--border-primary) bg-(--surface-action) body-sm-semibold text-(--font-primary) hover:bg-(--surface-primary-lowest) transition-colors"
                >
                  <img src={addIcon} alt="" />
                  {t('addAdditionalDocument')}
                </button>
                {/* TEMPORARY: need to be in anothe step */}
                {/* Comments textarea */}
                <div className="flex flex-col gap-2 mt-2">
                  <label className="body-sm-semibold text-(--font-body-2)">
                    {t('freeText')}
                  </label>
                  <textarea
                    value={contactClassification.comments || ''}
                    onChange={(e) => setValue(`contactClassifications.${contactClassificationIndex}.comments`, e.target.value)}
                    placeholder={t('freeTextPlaceholder')}
                    className="w-full p-3 border border-(--border-main) rounded-lg text-sm font-normal resize-y min-h-20 bg-(--surface-base) text-(--font-body-1) placeholder:text-(--font-body-3) focus:outline-none focus:border-(--border-secondary-strong)"
                    rows={3}
                  />
                </div>
              </div>
            </div>
          );
        })
      )}

    </div>
  );
};

export default DocumentsSection;

